import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import { useData } from "../../providers/dataProvider";

export default function ImageUploadModal({ show, onHide, onUploadSuccess }) {
  const { addImage, uploadProgress } = useData();
  const [uploadFile, setUploadFile] = useState(null);
  const [description, setDescription] = useState("");
  const [altText, setAltText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const resetForm = () => {
    setUploadFile(null);
    setDescription("");
    setAltText("");
    setUploadError(null);
    setUploading(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      // Use the addImage method from the useData hook
      const uploadedImage = await addImage(uploadFile, {
        description,
        altText,
      });

      // Notify parent component of successful upload
      if (onUploadSuccess) {
        onUploadSuccess(uploadedImage);
      }

      handleClose();
    } catch (err) {
      console.error("Error uploading image:", err);
      setUploadError(`Failed to upload image: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      contentClassName="bg-dark text-light"
      backdrop="static"
    >
      <Modal.Header closeButton className="border-secondary">
        <Modal.Title>Upload New Image</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleUploadSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Image File</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              required
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter image description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Alt Text (for accessibility)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter alt text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          {uploadProgress > 0 && (
            <div className="mb-3">
              <ProgressBar
                now={uploadProgress}
                label={`${Math.round(uploadProgress)}%`}
                variant="success"
                className="bg-dark"
              />
            </div>
          )}

          {uploadError && <Alert variant="danger">{uploadError}</Alert>}

          <div className="d-flex justify-content-end">
            <Button
              variant="outline-light"
              onClick={handleClose}
              className="me-2"
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
